import type { TranslationKey } from "@privacy/translations";
import { Box, Stack, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useTranslation } from "@privacy/translations";
import type { DashboardResponse, DataCardVariant, TopListVariant } from "../../api/dashboard";
import { useQuery } from "@tanstack/react-query";
import { dashboardQueryOptions, TopListVariantSchema } from "../../api/dashboard";
import { PageSpinner, PageWrapper } from "../../shared/Page";
import { usePrivacyDocumentTitle } from "../../shared/usePrivacyDocumentTitle";
import { useCurrentOrganisation } from "../../root/CurrentOrganisationProvider";
import { statusColors as registrationStatusColors } from "../../shared/RegistrationStatus";
import { multilineEllipsis } from "../../shared/multilineEllipsis";
import { StatusSquare } from "../../shared/RegistrationStatusDot";

export const DashboardPage = () => {
  usePrivacyDocumentTitle("Sidebar.Dashboard.PageTitle");
  const currentOrganisation = useCurrentOrganisation();
  const dashboardQuery = useQuery(dashboardQueryOptions());

  if (!dashboardQuery.data) return <PageSpinner />;

  return (
    <PageWrapper sx={{ maxWidth: "2400px", mx: "auto", my: 3 }}>
      <Typography
        variant="h1"
        sx={{ textAlign: "center", fontWeight: 400, fontSize: theme => theme.typography.pxToRem(48) }}
      >
        {currentOrganisation.name}
      </Typography>

      <Stack sx={{ gap: { xs: 10, xl: 16 } }}>
        <ProcessingsAsControllerByStatus
          processingsAsControllerByStatus={dashboardQuery.data.processingsAsControllerByStatus}
          numberOfSharedProcessings={dashboardQuery.data.numberOfSharedProcessings ?? 0}
        />
        <TopList topList={dashboardQuery.data.topList} />

        <DataCards dataCards={dashboardQuery.data.dataCards} />
      </Stack>
    </PageWrapper>
  );
};

const ProcessingsAsControllerByStatus = ({
  processingsAsControllerByStatus,
  numberOfSharedProcessings,
}: {
  processingsAsControllerByStatus: DashboardResponse["processingsAsControllerByStatus"];
  numberOfSharedProcessings: number;
}) => {
  const { t } = useTranslation();
  const segments = [
    ...processingsAsControllerByStatus.map(({ name, value }) => ({
      key: name,
      label: t(`RegistrationStatus.${name}`),
      value,
      background: registrationStatusColors[name].dot,
    })),
    ...(numberOfSharedProcessings > 0
      ? [
          {
            key: "fromLibrary",
            label: t("Dashboard.FromLibrary"),
            value: numberOfSharedProcessings,
            background: (theme: Theme) =>
              `repeating-linear-gradient(135deg, ${theme.palette.grey["400"]} 0 4px, ${theme.palette.grey["200"]} 4px 8px)`,
          },
        ]
      : []),
  ];
  const registrationCount = segments.reduce((total, { value }) => total + value, 0);
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "auto 1fr auto",
        mt: 6,
        px: { xs: 6, xl: 10 },
        py: { xs: 4, xl: 6 },
        gap: { xs: 6, xl: 10 },
        alignItems: "center",
        backgroundColor: theme => theme.palette.grey["100"],
        border: "1px solid",
        borderColor: theme => theme.palette.grey["200"],
      }}
    >
      <Stack sx={{ mr: { xs: 6, xl: 10 } }}>
        <Typography
          variant="h1"
          component="span"
          sx={{
            lineHeight: 1,
            fontSize: theme => ({ xs: theme.typography.pxToRem(64), xl: theme.typography.pxToRem(74) }),
            fontWeight: "600",
          }}
        >
          {registrationCount}
        </Typography>
        <Typography
          component="span"
          sx={{
            lineHeight: 1,
            mt: 0,
            width: "max-content",
            fontWeight: 400,
            color: theme => theme.palette.text.secondary,
            fontSize: theme => ({ xs: theme.typography.pxToRem(20), xl: theme.typography.pxToRem(26) }),
          }}
        >
          {t("Dashboard.TotalProcessings")}
        </Typography>
        {numberOfSharedProcessings > 0 && (
          <Typography sx={{ mt: 1.5, fontSize: theme => theme.typography.pxToRem(12), color: "text.secondary" }}>
            {t("Dashboard.OwnAndFromLibrary", {
              own: registrationCount - numberOfSharedProcessings,
              fromLibrary: numberOfSharedProcessings,
            })}
          </Typography>
        )}
      </Stack>
      <Box
        sx={{ width: "100%", flex: 1, display: "flex", height: "100%", gap: 0.5, borderRadius: 1, overflow: "hidden" }}
      >
        {segments.map(({ key, value, background }) => {
          const percentage = value > 0 ? (value / registrationCount) * 100 : 0;

          if (percentage === 0) return null;

          return <Box key={key} sx={{ height: "100%", width: `${percentage}%`, background }} />;
        })}
      </Box>
      <Stack
        component="ul"
        sx={{ gap: 1, listStyle: "none", p: 0, py: 1, m: 0, height: "100%", justifyContent: "space-between" }}
      >
        {segments.map(({ key, label, value, background }) => (
          <Stack direction="row" component="li" key={key} sx={{ gap: 2, alignItems: "center" }}>
            <StatusSquare sx={{ background }} />
            <Typography sx={{ fontSize: theme => theme.typography.pxToRem(12), fontWeight: 600, lineHeight: 1 }}>
              {label}
            </Typography>
            <Box
              sx={{
                fontVariantNumeric: "tabular-nums",
                pl: 5,
                ml: "auto",
                fontSize: theme => theme.typography.pxToRem(12),
                color: theme => theme.palette.text.tertiary,
                lineHeight: 1,
              }}
            >
              {value}
            </Box>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

const TopList = ({ topList }: { topList: DashboardResponse["topList"] }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: { xs: 2, xl: 4 } }}>
      {TopListVariantSchema.options.map(variant => {
        const { titleKey, descriptionKey, color } = topListVariantInfo[variant];
        const variantData = topList[variant];

        const largestValue = Math.max(...variantData.map(({ value }) => value));
        const totalCountForVariant = variantData.reduce((count, { value }) => count + value, 0);

        return (
          <Stack key={variant}>
            <Typography variant="h2" sx={{ fontWeight: 600, px: 2, whiteSpace: "nowrap" }}>
              {t(titleKey)}
              <Box component="span" sx={{ fontWeight: 400, color: theme => theme.palette.text.tertiary }}>
                {t(descriptionKey, { value: totalCountForVariant })}
              </Box>
            </Typography>
            <Stack
              component="ul"
              sx={{
                borderTop: "1px solid",
                borderColor: theme => theme.palette.grey["300"],
                listStyle: "none",
                p: 0,
                mt: 4,
              }}
            >
              {variantData.length === 0 ? (
                <Stack sx={{ alignItems: "center", justifyContent: "center", mt: 7 }}>
                  <Typography
                    sx={{
                      fontSize: theme => theme.typography.pxToRem(12),
                      color: theme => theme.palette.text.tertiary,
                    }}
                  >
                    {t("Dashboard.TopList.NoDataAvailable")}
                  </Typography>
                </Stack>
              ) : (
                variantData.map(({ name, value }, index) => (
                  <Box
                    component="li"
                    key={name}
                    sx={{
                      alignItems: "center",
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 44%) 1fr auto",
                      px: 2,
                      py: 1.25,
                      gap: 2,
                      borderBottom: "1px solid",
                      borderColor: theme => theme.palette.grey["300"],
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: "150px",
                        fontWeight: 600,
                        fontSize: theme => theme.typography.pxToRem(14),
                        ...multilineEllipsis(2),
                      }}
                    >
                      {index + 1}. {name}
                    </Typography>
                    <Box
                      sx={{
                        ml: "auto",
                        width: `${largestValue ? (value / largestValue) * 100 : 0}%`,
                        borderRadius: 1,
                        height: "6px",
                        backgroundColor: color,
                      }}
                    />
                    <Box
                      sx={{
                        fontVariantNumeric: "tabular-nums",
                        textAlign: "right",
                        width: `${largestValue.toString().length}ch`,
                        fontSize: theme => theme.typography.pxToRem(12),
                        color: theme => theme.palette.text.tertiary,
                        lineHeight: 1,
                        fontWeight: 600,
                      }}
                    >
                      {value}
                    </Box>
                  </Box>
                ))
              )}
            </Stack>
          </Stack>
        );
      })}
    </Box>
  );
};

const topListVariantInfo: Record<
  TopListVariant,
  { titleKey: TranslationKey; descriptionKey: TranslationKey; color: (theme: Theme) => string }
> = {
  categoriesOfPersonalData: {
    titleKey: "Dashboard.TopList.PersonalData.Title",
    descriptionKey: "Dashboard.TopList.PersonalData.Description",
    color: theme => theme.palette.brandBlue["500"],
  },
  categoriesOfDataSubject: {
    titleKey: "Dashboard.TopList.DataSubjects.Title",
    descriptionKey: "Dashboard.TopList.DataSubjects.Description",
    color: theme => theme.palette.brandBlue["500"],
  },
  units: {
    titleKey: "Dashboard.TopList.Units.Title",
    descriptionKey: "Dashboard.TopList.Units.Description",
    color: theme => theme.palette.brandOrange["500"],
  },
  systems: {
    titleKey: "Dashboard.TopList.Systems.Title",
    descriptionKey: "Dashboard.TopList.Systems.Description",
    color: theme => theme.palette.purple["600"],
  },
};
const DataCards = ({ dataCards }: { dataCards: DashboardResponse["dataCards"] }) => {
  const { t } = useTranslation();

  return (
    <Stack>
      <Typography variant="h2" sx={{ mb: 4, textAlign: "center" }}>
        {t("Dashboard.DataCards.Title")}
      </Typography>
      <Box
        sx={{
          "--min-card-width": "340px",
          "@media (min-width: 2000px)": { "--min-card-width": "420px" },
          "display": "grid",
          "gridTemplateColumns": "repeat(auto-fill, minmax(var(--min-card-width), 1fr))",
          "gap": { xs: 2, xl: 4 },
        }}
      >
        {dataCards.map(({ variant, value }) => {
          const { titleKey, descriptionKey } = dataCardsInfo[variant];
          return (
            <Stack
              key={variant}
              sx={{
                p: 4,
                border: theme => `1px solid ${theme.palette.grey["400"]}`,
                boxShadow: theme => `-2px 0 0 0 ${theme.palette.orange["500"]}`,
              }}
            >
              <Stack direction="row" sx={{ justifyContent: "space-between", gap: 2, alignItems: "center" }}>
                <Typography
                  sx={{
                    fontSize: theme => theme.typography.pxToRem(16),
                    fontWeight: 600,
                    lineHeight: 1.2,
                    color: theme => theme.palette.text.primary,
                  }}
                >
                  {t(titleKey)}
                </Typography>
                <Box
                  sx={{
                    lineHeight: 1.2,
                    alignSelf: "flex-start",
                    fontSize: theme => theme.typography.pxToRem(18),
                    color: theme => theme.palette.orange["500"],
                    fontWeight: 600,
                  }}
                >
                  {value}
                </Box>
              </Stack>
              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: theme => theme.typography.pxToRem(14),
                  color: theme => theme.palette.text.tertiary,
                }}
              >
                {t(descriptionKey, { value })}
              </Typography>
            </Stack>
          );
        })}
      </Box>
    </Stack>
  );
};

const dataCardsInfo: Record<DataCardVariant, { titleKey: TranslationKey; descriptionKey: TranslationKey }> = {
  thirdCountryTransfers: {
    titleKey: "Dashboard.DataCard.ThirdCountryTransfers.Title",
    descriptionKey: "Dashboard.DataCard.ThirdCountryTransfers.Description",
  },
  dataAboutChildren: {
    titleKey: "Dashboard.DataCard.DataAboutChildren.Title",
    descriptionKey: "Dashboard.DataCard.DataAboutChildren.Description",
  },
  dataAboutCrime: {
    titleKey: "Dashboard.DataCard.DataAboutCrime.Title",
    descriptionKey: "Dashboard.DataCard.DataAboutCrime.Description",
  },
  specialCategoriesOfPersonalData: {
    titleKey: "Dashboard.DataCard.SpecialCategories.Title",
    descriptionKey: "Dashboard.DataCard.SpecialCategories.Description",
  },
  processorsWithoutProcessorAgreements: {
    titleKey: "Dashboard.DataCard.ProcessorsWithoutAgreements.Title",
    descriptionKey: "Dashboard.DataCard.ProcessorsWithoutAgreements.Description",
  },
  systemsWithoutSecurityMeasures: {
    titleKey: "Dashboard.DataCard.SystemsWithoutSecurity.Title",
    descriptionKey: "Dashboard.DataCard.SystemsWithoutSecurity.Description",
  },
  automatedDecisionMaking: {
    titleKey: "Dashboard.DataCard.AutomatedDecisionMaking.Title",
    descriptionKey: "Dashboard.DataCard.AutomatedDecisionMaking.Description",
  },
  theDataSubjectsHaveNotBeenInformed: {
    titleKey: "Dashboard.DataCard.DataSubjectsInformed.Title",
    descriptionKey: "Dashboard.DataCard.DataSubjectsInformed.Description",
  },
};
